import { Injectable } from '@angular/core';
import { KeycloakService } from '@inoventoryapps/keycloak-angular';
import { SERVICE_URL } from '../../_urls/service.urls';

@Injectable({
    providedIn: 'root',
})
export class KeyService {
    private db!: IDBDatabase;
    derivedKey: any;
    keyClockJwk: any;
    fdmJwk: any;
    serverKeyRes: any;
    jwk: any;
    keyClockKeyRes: any;

    constructor(private keycloakService: KeycloakService) {}

    initDB(): Promise<IDBDatabase> {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open('crypto-keys', 2);
            request.onupgradeneeded = (event: any) => {
                this.db = event.target.result;
                if (!this.db.objectStoreNames.contains('keys')) {
                    this.db.createObjectStore('keys', { keyPath: 'name' });
                }
            };
            request.onsuccess = async (event: any) => {
                this.db = event.target.result;
                await this.generateKeys();
                resolve(this.db);
            };
            request.onerror = (event: any) => {
                reject(event.target.error);
            };
        });
    }

    async generateKeys() {
        const keyPair = await window.crypto.subtle.generateKey(
            { name: 'ECDH', namedCurve: 'P-256' },
            false,
            ['deriveKey', 'deriveBits']
        );
        const signKeyPair = await window.crypto.subtle.generateKey(
            { name: 'ECDSA', namedCurve: 'P-256' },
            false,
            ['verify', 'sign']
        );
        const transaction = this.db.transaction(['keys'], 'readwrite');
        const store = transaction.objectStore('keys');
        store.put({ name: 'client-key', key: keyPair });
        store.put({ name: 'sign-key', key: signKeyPair });
    }

    async getClientKey() {
        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction(['keys'], 'readonly');
            const store = transaction.objectStore('keys');
            const request = store.get('client-key');
            request.onsuccess = () => resolve(request.result?.key);
            request.onerror = () => reject(request.error);
        });
    }

    async getSignKey() {
        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction(['keys'], 'readonly');
            const store = transaction.objectStore('keys');
            const request = store.get('sign-key');
            request.onsuccess = () => resolve(request.result?.key);
            request.onerror = () => reject(request.error);
        });
    }

    private keyClockJwkPromise: Promise<any> | null = null;
    private serverJwkPromise: Promise<any> | null = null;

    getKid(url: any) {
        if (url.includes(SERVICE_URL.KEYCLOCK_SERVICE)) {
            return this.keyClockJwk?.keys?.[0]?.kid;
        }
        return this.fdmJwk?.keys?.[0]?.kid;
    }

    async getServerPublicKey(jwksUrl: string, url: any): Promise<CryptoKey> {
        if (url.includes(SERVICE_URL.KEYCLOCK_SERVICE)) {
            if (!this.keyClockKeyRes) {
                if (!this.keyClockJwkPromise) {
                    this.keyClockJwkPromise = fetch(jwksUrl)
                        .then((response) => response.json())
                        .catch((err) => {
                            this.keyClockJwkPromise = null;
                            throw err;
                        });
                }
                const jwks = await this.keyClockJwkPromise;
                this.keyClockJwk = jwks;
                this.keyClockKeyRes = jwks.keys[0];
            }
        } else {
            if (!this.serverKeyRes) {
                if (!this.serverJwkPromise) {
                    this.serverJwkPromise = fetch(jwksUrl)
                        .then((response) => response.json())
                        .catch((err) => {
                            this.serverJwkPromise = null;
                            throw err;
                        });
                }
                const jwks = await this.serverJwkPromise;
                this.fdmJwk = jwks;
                this.serverKeyRes = jwks.keys[0];
            }
        }

        return crypto.subtle.importKey(
            'jwk',
            url.includes(SERVICE_URL.KEYCLOCK_SERVICE)
                ? this.keyClockKeyRes
                : this.serverKeyRes,
            { name: 'ECDH', namedCurve: 'P-256' },
            true,
            []
        );
    }

    async signJwt(header: any, method: string, url: string) {
        const keyPair: any = await this.getSignKey();
        const clientKeyPair: any = await this.getClientKey();
        const privateKey = keyPair.privateKey;

        const JWKDPOPPublicKey = await window.crypto.subtle.exportKey(
            'jwk',
            keyPair.publicKey
        );
        const JWKClientPublicKey = await window.crypto.subtle.exportKey(
            'jwk',
            clientKeyPair.publicKey
        );

        const dpopHeader = {
            typ: 'dpop+jwt',
            alg: 'ECDSA',
            jwk: JWKDPOPPublicKey,
        };

        const payload = {
            jti: crypto.randomUUID(),
            htm: method,
            htu: url.split('?')[0].split('#')[0],
            iat: Math.floor(Date.now() / 1000),
            ath: await this.hashToken(await this.keycloakService.getToken()),
            enc: {
                jwk: JWKClientPublicKey,
                kid: this.serverKeyRes?.kid,
                iv: this.arrayBufferToBase64(header.iv),
            },
        };

        const encoder = new TextEncoder();
        const encodedHeader = this.arrayBufferToBase64(
            encoder.encode(JSON.stringify(dpopHeader))
        );
        const encodedPayload = this.arrayBufferToBase64(
            encoder.encode(JSON.stringify(payload))
        );

        const data = encoder.encode(`${encodedHeader}.${encodedPayload}`);
        const signature = await crypto.subtle.sign(
            { name: 'ECDSA', hash: { name: 'SHA-256' } },
            privateKey,
            data
        );

        const encodedSignature = this.arrayBufferToBase64(
            new Uint8Array(signature)
        );
        return `${encodedHeader}.${encodedPayload}.${encodedSignature}`;
    }

    async hashToken(jwtToken: string): Promise<string> {
        const encoder = new TextEncoder();
        const data = encoder.encode(jwtToken);
        const hashBuffer = await window.crypto.subtle.digest('SHA-256', data);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        return hashArray
            .map((byte) => byte.toString(16).padStart(2, '0'))
            .join('');
    }

    arrayBufferToBase64(buffer: any) {
        let binary = '';
        const bytes = new Uint8Array(buffer);
        const len = bytes.byteLength;
        for (let i = 0; i < len; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        return window.btoa(binary);
    }

    base64ToArrayBuffer(base64: any) {
        const binaryString = atob(base64);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }
        return bytes.buffer;
    }

    objectToBase64(obj: any): string {
        const json = JSON.stringify(obj);
        const utf8Bytes = new TextEncoder().encode(json);
        let binary = '';
        utf8Bytes.forEach((byte) => (binary += String.fromCharCode(byte)));
        return btoa(binary);
    }

    base64ToObject(base64: string): any {
        const binary = atob(base64);
        const bytes = new Uint8Array(
            [...binary].map((char) => char.charCodeAt(0))
        );
        const json = new TextDecoder().decode(bytes);
        return JSON.parse(json);
    }

    async decodeResponse(response: any, xCryptoEnabled: any) {
        const encryptedData = this.base64ToArrayBuffer(response);
        const ivFromHeader = this.base64ToArrayBuffer(xCryptoEnabled);
        try {
            const decryptedBody = await crypto.subtle.decrypt(
                { name: 'AES-GCM', iv: ivFromHeader },
                this.derivedKey,
                encryptedData
            );
            const decoder = new TextDecoder();
            const decodedBody = decoder.decode(decryptedBody);
            return JSON.parse(decodedBody);
        } catch (error) {
            console.error('Error while decrypting response', error);
            return {};
        }
    }
}
